import { container } from 'tsyringe';

import { TOKENS } from '../di/container';

import type {
  IStorageService,
  ISyncStorageService,
} from '../types/common/storage.types';
import type { PersistStorage, StorageValue } from 'zustand/middleware';

/**
 * Cross-platform persist storage for zustand `persist` stores.
 *
 * Routes persistence entirely through the DI container — `packages/shared` holds
 * NO reference to any platform storage (no `localStorage`, no `window`). Each app
 * registers its own implementation at startup:
 *
 * - Web registers a synchronous `ISyncStorageService` (localStorage-backed) via
 *   `registerSyncStorageService`, so stores hydrate SYNCHRONOUSLY at creation —
 *   matching the browser's storage semantics and avoiding a logged-out flash.
 * - Mobile registers the async `IStorageService` (AsyncStorage-backed) via
 *   `registerStorageService`; hydration there is asynchronous.
 *
 * The adapter prefers the sync service when present, then the async one. If
 * neither is registered (e.g. before bootstrap, or SSR) it is a safe no-op —
 * persistence is simply disabled until a service is registered.
 *
 * NOTE: web must register its sync service BEFORE any persist store module is
 * imported (stores hydrate at module-eval time). See the web bootstrap.
 */
export function createPersistStorage<S>(): PersistStorage<S> {
  const syncService = (): ISyncStorageService | null =>
    container.isRegistered(TOKENS.SYNC_STORAGE)
      ? container.resolve<ISyncStorageService>(TOKENS.SYNC_STORAGE)
      : null;

  const asyncService = (): IStorageService | null =>
    container.isRegistered(TOKENS.STORAGE)
      ? container.resolve<IStorageService>(TOKENS.STORAGE)
      : null;

  return {
    getItem: (
      name
    ): StorageValue<S> | null | Promise<StorageValue<S> | null> => {
      const syncSvc = syncService();
      if (syncSvc) {
        return syncSvc.get<StorageValue<S>>(name) ?? null;
      }
      const asyncSvc = asyncService();
      if (asyncSvc) {
        return asyncSvc
          .get<StorageValue<S>>(name)
          .then((value) => value ?? null);
      }
      return null;
    },

    setItem: (name, value): void | Promise<void> => {
      const syncSvc = syncService();
      if (syncSvc) {
        syncSvc.set(name, value);
        return;
      }
      const asyncSvc = asyncService();
      if (asyncSvc) {
        return asyncSvc.set(name, value);
      }
      return undefined;
    },

    removeItem: (name): void | Promise<void> => {
      const syncSvc = syncService();
      if (syncSvc) {
        syncSvc.remove(name);
        return;
      }
      const asyncSvc = asyncService();
      if (asyncSvc) {
        return asyncSvc.remove(name);
      }
      return undefined;
    },
  };
}
